import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  History,
  Footprints,
  Wrench,
  ServerCog,
  Compass,
  ScrollText,
  BookOpen,
  HelpCircle,
  Map as MapIcon,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    label: 'WebCenter 11g Premier Support',
    value: 'Ended years ago',
    detail:
      'Oracle Fusion Middleware 11g Premier Support ended in 2018. Extended Support has since wound down. 11g production installs today are running on Sustaining Support or unsupported configurations.',
    tone: 'red',
  },
  {
    label: 'WebCenter 12c support window',
    value: 'Ends Dec 2026 / Dec 2027',
    detail:
      'Oracle Fusion Middleware 12c Premier Support ends December 2026. Extended Support runs only 12 more months and ends December 2027. 12c is a staging point, not a destination.',
    tone: 'amber',
  },
  {
    label: 'WebCenter 14c support window',
    value: 'Through 2030 / 2033',
    detail:
      'Oracle Fusion Middleware 14c (14.1.2 LTS) is supported through December 2030 (Premier) and December 2033 (Extended). Landing on 14c buys approximately 8 years of supported runway.',
    tone: 'green',
  },
];

const UPGRADE_STEPS = [
  {
    step: '1',
    title: 'Inventory the 11g implementation',
    body: 'Document every 11g WebCenter Content component, every WebCenter Imaging configuration, every Oracle Document Capture / WFR project, every SOA 11g composite, every ADF 11g customization, every WebLogic 10.3.x domain, and every EBS or Fusion integration point. 11g installs typically have less documentation than 12c installs — the archeology pass is longer.',
  },
  {
    step: '2',
    title: 'Upgrade 11g → 12c (the staging step)',
    body: 'Oracle does not support a direct 11g → 14c upgrade. The supported path is 11g → 12c first, using the 12c Upgrade Assistant. WebCenter Content, Imaging, Capture, Forms Recognition, SOA, ADF, and WebLogic each have their own 11g → 12c migration procedure. Deprecated 11g features (Jive in Portal, certain SOA 11g adapters, ADF 11g patterns) are surfaced and replaced at this step.',
  },
  {
    step: '3',
    title: 'Stabilize on 12c briefly',
    body: 'A short stabilization window on 12c is recommended — long enough to validate that every customization, workflow, and integration behaves correctly after the 11g → 12c migration, but short enough that the team carries momentum into the 12c → 14c step.',
  },
  {
    step: '4',
    title: 'Upgrade 12c → 14c (out-of-place domain directory)',
    body: 'Oracle\'s recommended 12c → 14c pattern is an out-of-place domain directory upgrade using the 14c Upgrade Assistant. A new 14c domain is created alongside the 12c domain; configuration is migrated forward; custom components, Forms Recognition projects, SOA composites, ADF customizations, and WebLogic deployments are re-deployed and validated.',
  },
  {
    step: '5',
    title: 'Java, database, and OCI tier upgrades',
    body: 'WebLogic 14c runs on Java 17 / Java 21. Database tier upgrades to 19c or higher. Many 11g shops are simultaneously off-supported on Oracle Database. If the target is OCI, network, IAM, and Object Storage tiers are set up at this step.',
  },
  {
    step: '6',
    title: 'Parallel run, UAT, cutover',
    body: 'Run 11g (or 12c-staging) and 14c in parallel during UAT. Validate every WebCenter Content retrieval, every Forms Recognition extraction, every SOA composite, every ERP posting path. Cut over only after end-to-end validation against production-shaped volume.',
  },
];

const SCENARIOS = [
  {
    icon: ScrollText,
    title: 'Heavy 11g customization that nobody has docs for',
    detail:
      'The original implementation team is long gone. iDoc Script, custom WCC components, custom Java services, and custom SOA composites were built against 11g APIs. There is no written architecture. The forward path starts with reading the running 11g implementation directly, not with a discovery questionnaire.',
  },
  {
    icon: Wrench,
    title: 'ADF 11g forms that may not survive a Java 17 / 21 migration',
    detail:
      'ADF 11g customizations — especially the non-PO coding form patterns common in WebCenter Imaging AP — were written against an older ADF runtime and an older Java. 14c WebLogic runs on Java 17 / Java 21. Each ADF 11g customization needs an explicit 14c compatibility review and, in many cases, a forward port.',
  },
  {
    icon: ServerCog,
    title: 'SOA 11g composites with deprecated adapters',
    detail:
      'SOA Suite 11g composites often relied on adapters and patterns that are deprecated or removed in 12c and 14c. Each composite needs an inventory pass: which adapters, which patterns, which transformations carry forward, and which need to be re-implemented before 14c.',
  },
  {
    icon: History,
    title: '11g + EBS R12.1.x (also out of Premier Support)',
    detail:
      'Many 11g WebCenter customers run on EBS R12.1.x, which itself is past Premier Support. The forward path frequently surfaces a parallel EBS decision: stay on R12.1.x under Market Driven Support, upgrade to EBS 12.2, or move to Fusion Cloud ERP. The WebCenter upgrade and the EBS upgrade are coupled.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    eyebrow: 'Tier 1 · 11g Inventory & Decision Read',
    title: 'Fixed-fee read of the 11g implementation',
    body: 'Andrew reads your existing 11g WebCenter implementation directly — WCC components, Imaging configuration, Capture / WFR projects, SOA 11g composites, ADF 11g customizations, WebLogic 10.3.x domains, EBS or Fusion integration. Output: structured inventory and a forward-path recommendation (11g → 12c → 14c, or modernize off WebCenter). The inventory is yours to keep regardless of which delivery partner you choose.',
  },
  {
    eyebrow: 'Tier 2 · Two-Step Upgrade Planning',
    title: 'Full 11g → 12c → 14c upgrade plan',
    body: 'Detailed upgrade plan covering both the 11g → 12c staging step and the 12c → 14c step. Surfaces deprecated 11g features (Jive in Portal, removed SOA adapters, ADF 11g compatibility breaks), Java 17 / 21 implications for ADF and WebLogic, database tier upgrade requirements, and integration re-test scope. Sized for the customer who has already chosen the upgrade path and needs a defendable plan for procurement and Oracle.',
  },
  {
    eyebrow: 'Tier 3 · Forward Deployed Engineering',
    title: 'Founder-led execution through the two-step upgrade',
    body: 'For customers who do not want to assemble a 12-month vendor engagement: Andrew embeds as Forward Deployed Engineer through the 11g → 12c staging step and the 12c → 14c step. Founder-level WebCenter expertise applied directly to your stack, with your team learning the 14c architecture in real time. Sized per situation.',
  },
  {
    eyebrow: 'Tier 4 · Strategic Advisory Retainer',
    title: 'Founder-level guidance through your upgrade window',
    body: 'Monthly retainer with Andrew for the customer working through the 11g decision with internal stakeholders, procurement, and Oracle. Strategic guidance on the two-step upgrade tradeoffs, the Path B modernization alternative, customization decisions, and 14c-specific architecture choices. Does not replace delivery work — informs it.',
  },
];

const FAQS = [
  {
    q: 'Can we go directly from 11g to 14c?',
    a: 'No — Oracle does not support a direct 11g → 14c upgrade for the WebCenter / Fusion Middleware stack. The supported path is 11g → 12c → 14c, with 12c as a staging point. The 11g → 12c step uses the 12c Upgrade Assistant; the 12c → 14c step uses the 14c Upgrade Assistant with the out-of-place domain directory pattern. The two steps can be sequenced as a single continuous project.',
  },
  {
    q: 'Is the two-step upgrade slower than just modernizing off WebCenter?',
    a: 'The two-step upgrade is often faster end-to-end (8-14 months) than full modernization for relatively clean 11g installs with documented customizations and a contained integration surface. For an 11g install with deep undocumented customization, deprecated adapters, and tight EBS coupling, modernizing off WebCenter (Path B) can be the more direct path. The Inventory & Decision Read is designed to surface which side of that line a specific implementation falls on.',
  },
  {
    q: 'What about WebCenter Portal 11g?',
    a: 'WebCenter Portal 11g includes features that were deprecated in 12c and removed entirely in 14c — notably the Jive features. If WebCenter Portal 11g is in scope, the inventory pass needs to explicitly surface which Portal features are in use and which have an alternative collaboration pattern in 14c. In some cases the Portal layer is the part that does not carry forward and needs a separate decision from the rest of the WebCenter stack.',
  },
  {
    q: 'What is the ADF 11g compatibility situation?',
    a: 'ADF 11g customizations typically need an explicit compatibility review against ADF 14c and, in many cases, a forward port — especially custom non-PO invoice coding forms and custom skins. ADF 11g was written against an older ADF runtime, an older JDK, and older browser assumptions; ADF 14c runs against Java 17 / Java 21. ADF skin work in particular often does not survive the migration without rework. The inventory pass identifies every ADF customization and classifies it as carry-forward, port, or rewrite.',
  },
  {
    q: 'What is the SOA 11g compatibility situation?',
    a: 'SOA Suite 11g composites often used adapters and integration patterns that were deprecated in 12c and are not available in 14c. Each composite needs an inventory pass: source and target adapters, transformation logic, error handling, and human-task patterns. Some composites migrate forward cleanly via the Upgrade Assistant; others need re-implementation. The deprecated-adapter list is the single biggest source of surprises in an 11g → 12c step.',
  },
  {
    q: 'How does WebLogic 11g (10.3.x) handle Java 17 / 21?',
    a: 'WebLogic 14c runs on Java 17 / Java 21, while WebLogic 10.3.x (the 11g runtime) was certified against much older JDK versions — so the migration crosses several JDK boundaries at once. Custom MBeans, custom security providers, custom JMS configurations, and any Java code compiled against the 11g API need recompilation and validation. The domain configuration itself migrates forward via the Upgrade Assistant; the running Java surface needs explicit attention.',
  },
  {
    q: 'What is the typical timeline for an 11g → 12c → 14c project?',
    a: 'Typical timelines for the full two-step upgrade range 8-14 months depending on stack complexity. Implementations with extensive Forms Recognition template libraries, sophisticated SOA composite workflows, heavy ADF non-PO coding form customizations, or a parallel EBS upgrade in scope land at the longer end. Implementations closer to a reference architecture land at the shorter end. The Inventory & Decision Read typically runs 2-4 weeks and surfaces the right timeline for your specific implementation.',
  },
  {
    q: 'What does an 11g → 14c upgrade cost?',
    a: 'EZ Cloud establishes pricing per situation on a Decision Call, after enough of the implementation is understood to scope responsibly. Cost depends on stack complexity, customization depth, integration surface, and whether the upgrade is delivered as Standard Consulting or as Forward Deployed Engineering. The Inventory & Decision Read is the first commercial step and produces a defendable budget range for the full two-step project.',
  },
];

const WebCenter11gTo14c = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to upgrade Oracle WebCenter from 11g to 14c',
    description: 'The two-step upgrade pattern for Oracle WebCenter 11g customers. 11g → 12c as a staging step, then 12c → 14c, landing on WebCenter 14c (14.1.2 LTS).',
    totalTime: 'P18M',
    step: UPGRADE_STEPS.map((s, idx) => ({
      '@type': 'HowToStep',
      position: idx + 1,
      name: s.title,
      text: s.body,
      url: 'https://ezcloud.ai/solutions/webcenter-11g-to-14c#step-' + (idx + 1),
    })),
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter 11g to 14c Upgrade Path — Skip 12c, Land on Long-Term Support | EZ Cloud',
    description:
      'Oracle WebCenter 11g customers face a long-deferred decision. Premier Support on 11g ended years ago. The forward path is the upgrade to WebCenter 14c (14.1.2 LTS) — through 12c as a staging point. EZ Cloud delivers 11g → 12c → 14c upgrade planning, customization migration, and OCI deployment.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-11g-to-14c',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter 11g' },
      { '@type': 'Thing', name: 'Oracle WebCenter 14c Upgrade' },
      { '@type': 'Thing', name: 'Oracle WebCenter 12c' },
      { '@type': 'Thing', name: 'Oracle Fusion Middleware 11g' },
      { '@type': 'Thing', name: 'Oracle Upgrade Path' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter 11g to 14c Upgrade Path — Skip 12c, Land on Long-Term Support | EZ Cloud"
        description="Oracle WebCenter 11g customers face a long-deferred decision. Premier Support on 11g ended years ago. The forward path is the upgrade to WebCenter 14c (14.1.2 LTS) — through 12c as a staging point. EZ Cloud delivers 11g → 12c → 14c upgrade planning, customization migration, and OCI deployment."
        canonical="https://ezcloud.ai/solutions/webcenter-11g-to-14c"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter 11g to 14c Upgrade',
            url: 'https://ezcloud.ai/solutions/webcenter-11g-to-14c',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <History className="w-4 h-4" aria-hidden />
              WebCenter 11g · The long-deferred decision
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter 11g to 14c upgrade
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                For the customers who never upgraded from 11g.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              The forward path through 12c as a staging point, landing on WebCenter 14c (14.1.2
              LTS) with approximately 8 years of supported runway.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-11g-to-14c-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book an 11g Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What this is
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              The 11g → 14c forward path, in one paragraph
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter 11g Premier Support ended years ago.
                </strong>{' '}
                12c Premier Support ends December 2026, with Extended Support ending December
                2027. The forward destination is WebCenter 14c (release 14.1.2 LTS), which carries
                approximately 8 years of supported runway — Premier Support through December 2030
                and Extended Support through December 2033.
              </p>
              <p>
                <strong className="text-gray-900">
                  Oracle does not support a direct 11g → 14c upgrade.
                </strong>{' '}
                The supported pattern is 11g → 12c → 14c, with 12c as a staging point. The two
                steps can be sequenced as a single continuous project: 11g → 12c using the 12c
                Upgrade Assistant, a brief stabilization on 12c, then 12c → 14c using the 14c
                Upgrade Assistant with the out-of-place domain directory pattern.
              </p>
              <p>
                For some 11g implementations — particularly those with deep undocumented
                customization or tight coupling to an EBS R12.1.x instance that is itself past
                Premier Support — modernization off WebCenter (Path B) is the more direct forward
                path than two upgrade steps. The right answer depends on the specific
                implementation.
              </p>
            </div>
          </div>
        </section>

        {/* Key facts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The lifecycle frame
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Where 11g, 12c, and 14c sit on the Oracle support timeline
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Per Oracle's Lifetime Support Policy. 11g is well past Premier Support; 12c is the
              staging point; 14c is the long-term destination.
            </p>
            <div className="space-y-5">
              {KEY_FACTS.map((d) => (
                <Card
                  key={d.label}
                  className={`p-7 bg-white border shadow-sm ${
                    d.tone === 'red'
                      ? 'border-red-200'
                      : d.tone === 'amber'
                        ? 'border-amber-200'
                        : 'border-green-200'
                  }`}
                >
                  <div className="flex flex-col sm:flex-row gap-5 items-start">
                    <div
                      className={`flex-shrink-0 px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide ${
                        d.tone === 'red'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : d.tone === 'amber'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-green-50 text-green-700 border border-green-200'
                      }`}
                    >
                      {d.value}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{d.label}</h3>
                      <p className="text-gray-700 leading-relaxed">{d.detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Where 11g customers are today */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              Where customers are
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
              Many 11g installs are still in production
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Across the enterprises that adopted WebCenter Content, Imaging, Capture, Forms
                Recognition, SOA, ADF, and Portal in the 11g era, many production installs never
                made the jump to 12c. The reasons vary by organization but cluster into four
                patterns.
              </p>
              <p>
                <strong className="text-gray-900">Business stability.</strong> The 11g
                implementation runs. AP cycles close. The board has not asked for a refresh. The
                upgrade conversation gets re-scoped every budget cycle.
              </p>
              <p>
                <strong className="text-gray-900">Customization depth.</strong> The original
                implementation included iDoc Script customizations, custom WCC components, custom
                ADF taskflows, custom SOA composites, and FIPSA-style accelerators. The team that
                built them has rotated out. Upgrading means understanding work that nobody
                currently on the team did.
              </p>
              <p>
                <strong className="text-gray-900">Undocumented implementations.</strong> Reading a
                running 11g WebCenter implementation directly — the iDoc Script, the custom
                components, the SOA composites — is reverse-engineering work when the original
                architecture was never written down. Internal teams often bring in a senior engineer
                to start the inventory pass on that kind of work.
              </p>
              <p>
                <strong className="text-gray-900">ERP coupling.</strong> Many 11g WebCenter installs
                are tightly coupled to EBS R12.1.x. The WebCenter upgrade conversation surfaces a
                parallel EBS conversation, and the combined scope becomes a bigger project than
                anyone wanted to greenlight.
              </p>
            </div>
          </div>
        </section>

        {/* The two-step upgrade pattern */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              The two-step pattern
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              11g → 12c → 14c, sequenced as one project
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Oracle does not support a direct 11g → 14c upgrade. 12c is the staging point. The
              two steps can be run sequentially with a brief stabilization in between — typically
              as a single continuous project rather than two separate engagements.
            </p>
            <div className="space-y-5">
              {UPGRADE_STEPS.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the 12c end-of-support decision frame
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* What 11g shops face that 12c shops don't */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What 11g shops face that 12c shops don't
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The extra surface that comes with starting from 11g
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Customers on 12c are dealing with a single upgrade step and a known starting
              architecture. Customers on 11g are dealing with several deprecated patterns at once.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <h3 className="text-gray-900 font-semibold mb-2">
                  Jive features in WebCenter Portal 11g
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  The Jive-based collaboration features in WebCenter Portal 11g were deprecated in
                  12c and removed entirely in 14c. 12c-starting customers already replaced or
                  retired these. 11g-starting customers handle this during the 11g → 12c step.
                </p>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <h3 className="text-gray-900 font-semibold mb-2">
                  WebLogic 10.3.x → 14c (multi-JDK boundary)
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  WebLogic 11g (10.3.x) was certified against much older JDKs. WebLogic 14c runs
                  on Java 17 / Java 21. The migration crosses multiple JDK boundaries — every
                  piece of compiled Java in the domain needs validation.
                </p>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <h3 className="text-gray-900 font-semibold mb-2">
                  ADF 11g taskflows, skins, and managed beans
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  ADF 11g customizations — including the custom non-PO invoice coding forms common
                  in WebCenter Imaging AP — were written against an older ADF runtime. Each
                  customization needs a compatibility review against ADF 14c and, in many cases,
                  a forward port.
                </p>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <h3 className="text-gray-900 font-semibold mb-2">
                  SOA 11g composites with deprecated adapters
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  SOA 11g composites often used adapters and integration patterns that were
                  deprecated in 12c and are not available in 14c. Each composite needs an
                  inventory pass; some migrate forward via the Upgrade Assistant, others need
                  re-implementation.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Fast-track vs deliberate */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Two-step upgrade vs modernize off WebCenter
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              For some 11g customers, modernization is the more direct path
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The two-step upgrade preserves the WebCenter investment. For implementations with
              deep undocumented customization or tight EBS R12.1.x coupling, modernizing the AP
              automation layer off WebCenter entirely can be faster end-to-end than two upgrade
              steps.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border border-gray-200 rounded-xl overflow-hidden bg-white">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-1/5">
                      Dimension
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Two-step upgrade (11g → 12c → 14c)
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-wide text-accent px-5 py-4 w-2/5">
                      Modernize off WebCenter (Path B)
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-gray-200">
                    <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                      Typical timeline
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      8-14 months end-to-end (both upgrade steps + stabilization + UAT)
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      4-9 months end-to-end; AP layer replaced, Oracle ERP preserved
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                      Customizations
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      Carried forward into 12c then 14c with validation work at each step
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      Translated forward into the modern platform; some simplify away entirely
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                      Talent dependency
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      Needs 11g WebCenter expertise plus 12c plus 14c
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      Needs 11g WebCenter expertise once (for translation), then standard skills
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                      Next decision point
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      WebCenter 14c Premier Support ends Dec 2030 — same lifecycle again
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      Strategic decision made once
                    </td>
                  </tr>
                  <tr>
                    <td className="px-5 py-4 font-semibold text-gray-900 bg-gray-50">
                      Best fit when
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      Customizations are documented, integrations are stable, WebCenter is the
                      committed forward platform
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      Deep undocumented customization, tight EBS R12.1.x coupling, or the AP team
                      wants a modern AI-driven platform
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the standalone 12c → 14c upgrade page
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Common 11g scenarios */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common 11g scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four scenarios that surface in nearly every 11g Decision Call
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              These are the implementation patterns we hear most often from 11g customers. Each
              one shapes the forward path differently.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/oracle-weblogic-14c-upgrade"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the WebLogic 14c upgrade detail
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Engagement tiers */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on 11g → 14c
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four engagement tiers for the 11g forward path
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              11g installs typically have less documentation than 12c installs. Engagements lead
              with archeology — reading the running implementation directly — before any forward
              plan is committed. Standard Consulting and Forward Deployed Engineering are both
              available as delivery modes.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card key={t.eyebrow} className="p-7 bg-white border border-gray-200 shadow-sm">
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.eyebrow}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Engagement pricing is established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths — including the 11g → 12c → 14c
              two-step upgrade — with stack-specific considerations, eight diagnostic questions,
              and a vendor evaluation checklist.
            </p>
            <Link
              to="/resources/webcenter-decision-guide"
              className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
            >
              <BookOpen className="w-4 h-4 mr-2" aria-hidden />
              Read the Decision Guide
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
            </Link>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              <HelpCircle className="inline w-4 h-4 mr-1 -mt-1" aria-hidden /> Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on the 11g → 14c forward path
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk through your 11g → 14c forward path
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle WebCenter specialist with 20+ years specifically across WebCenter Content,
              Imaging, Capture, Forms Recognition, SOA, WebLogic (11g, 12c, 14c), and ADF. Walk
              through your 11g implementation and the upgrade considerations in the context of
              your specific stack — including the EBS coupling and the Path B alternative.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('webcenter-11g-to-14c-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Book an 11g Decision Call
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  <Compass className="w-4 h-4 mr-2 inline" aria-hidden />
                  See the full modernization path
                </Link>
              </Button>
            </div>
            <p className="sr-only">
              Related: <Footprints className="inline w-3 h-3" aria-hidden />
              <MapIcon className="inline w-3 h-3" aria-hidden />
            </p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenter11gTo14c;
